// Onboarding tour — a multi-step modal that explains the two main workflows
// (Projects + Single Unit), where each file is located, how to change conditions
// and limits, and how to find documentation. Slides through with Prev/Next.
//
// State is persisted to localStorage so first-time users auto-launch the tour
// on /home, but only once (subsequent launches must be manual via Help menu).

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Home as HomeIcon,
  FolderOpen,
  Gauge,
  ListChecks,
  Thermometer,
  Sliders,
  BarChart3,
  Layers,
  CalendarRange,
  Package,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  FileText,
  Wrench,
  Beaker,
  Library,
  Mail,
  MessageSquare,
} from "lucide-react";

// Each step has an icon (left rail), title, body (paragraphs), and an
// optional "where to click" hint pinned to the bottom.
//
// Body content is intentionally structured as React nodes — easier to nest
// lists / bold / code than templating strings.
const STEPS = [
  {
    key: "welcome",
    icon: HomeIcon,
    title: "Welcome to ZAMBA Selection Tool",
    body: (
      <>
        <p>
          This tool helps you select ZAMBA HVAC equipment for a project: from
          a single one-off unit lookup to a full multi-room submittal package
          with drawings, schedules and Selection Reports.
        </p>
        <p>
          The tour takes ~2 minutes. You can re-open it any time from{" "}
          <span className="inline-flex items-center px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
            Help → Start guided tour
          </span>{" "}
          in the top right.
        </p>
      </>
    ),
  },
  {
    key: "home",
    icon: HomeIcon,
    title: "Home — pick a workflow",
    body: (
      <>
        <p>After login you land on the Home page with two cards:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Projects</strong> — multi-room selections that are saved
            to your account. Each project goes through 8 wizard steps and
            can be exported as a full submittal.
          </li>
          <li>
            <strong>Single Unit Selection</strong> — quick one-off lookup. Pick
            a model, choose either <em>"max performance for a given length"</em>{" "}
            or <em>"meet required performance"</em>, run, see the results. No save.
          </li>
        </ul>
      </>
    ),
  },
  {
    key: "wizard-overview",
    icon: ListChecks,
    title: "The Project Wizard — 8 steps",
    body: (
      <>
        <p>Inside a project, the wizard guides you through:</p>
        <ol className="list-decimal pl-5 space-y-1 text-sm">
          <li><strong>Project</strong> — name, number, address.</li>
          <li><strong>Room Details</strong> — rooms with cooling / heating load + OA / EA.</li>
          <li><strong>Design Conditions</strong> — outdoor + indoor + water.</li>
          <li><strong>Advanced</strong> — glycol, air-on overrides, length caps, PA inputs.</li>
          <li><strong>Summary</strong> — per-room selections + Delivered % vs target.</li>
          <li><strong>Configurator</strong> — group rooms into variants.</li>
          <li><strong>Schedule</strong> — final assignment + BOM.</li>
          <li><strong>Submittal</strong> — compile the document package.</li>
        </ol>
      </>
    ),
  },
  {
    key: "rooms",
    icon: Layers,
    title: "Room Details — bulk operations",
    body: (
      <>
        <p>
          The Room Details step (Step 2) is the source of truth for what
          you want to cool / heat. Use the leftmost checkbox column to{" "}
          <strong>multi-select</strong> rooms; once 1+ rows are checked a
          dark toolbar appears with <em>Set Unit Model</em> and{" "}
          <em>Delete Selected</em>.
        </p>
        <p>
          You can also <strong>Import from CSV / XLSX</strong> (the
          download-template button generates a starter file) or{" "}
          <strong>Make all units same length</strong> from the Schedule tab.
        </p>
      </>
    ),
  },
  {
    key: "conditions",
    icon: Thermometer,
    title: "Design Conditions — where the climate lives",
    body: (
      <>
        <p>
          Step 3 sets the climate envelope: outdoor summer / winter DB-WB,
          indoor room DB / RH, and water entering temperature for cooling
          and heating modes.
        </p>
        <p>
          Picking an <strong>ASHRAE city</strong> auto-fills the outdoor
          design conditions — type into the search and pick from the
          dropdown. Pick <em>"Other"</em> if your country isn't listed.
        </p>
      </>
    ),
  },
  {
    key: "advanced",
    icon: Sliders,
    title: "Advanced — limits & overrides",
    body: (
      <>
        <p>
          Step 4 is where you tighten the selection. Most useful fields:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li><strong>Maximum Length per Model</strong> — caps the picker; engine will never pick a longer coil. Used when the room can only fit X feet of casework.</li>
          <li><strong>Glycol type & %</strong> — affects water-side capacity and PD curves.</li>
          <li><strong>Air-On override</strong> — fix the air-on temperature instead of letting the engine derive it from mixing.</li>
          <li><strong>Primary Air conditions</strong> — DB / RH / CFM at the PA inlet (L-models only).</li>
        </ul>
      </>
    ),
  },
  {
    key: "summary",
    icon: BarChart3,
    title: "Summary — per-room results",
    body: (
      <>
        <p>
          Step 5 shows one row per room with{" "}
          <strong>Total / Total Sensible / Total Latent</strong>{" "}
          capacities plus the chosen Model, Length and Delivered %.
        </p>
        <p>
          Click any row to drill into the individual report (chart +
          per-mode tables + water side). Edit <strong>QTY</strong> here
          to split a room across multiple identical units — the engine
          will pick a shorter length when QTY goes up.
        </p>
      </>
    ),
  },
  {
    key: "configurator",
    icon: Wrench,
    title: "Configurator — group into variants",
    body: (
      <>
        <p>
          Step 6 groups identical Model + Length combinations into named{" "}
          <strong>variants</strong> (e.g. EWS-1, EWS-2 for L-L 3 ft / 4 ft).
        </p>
        <p>
          For each variant you set Inclusions/Exclusions (factory options,
          field-installed accessories) which carry through to the submittal
          and BOM.
        </p>
      </>
    ),
  },
  {
    key: "schedule",
    icon: CalendarRange,
    title: "Schedule — final assignment & BOM",
    body: (
      <>
        <p>
          Step 7 maps every room to a variant and computes the bill of
          materials. Use the <strong>Variant Summary</strong> footer to
          confirm total qty and the per-row QTY input to fine-tune the split.
        </p>
        <p>
          One click on <strong>Save Config File</strong> exports both XLSX
          and CSV; <strong>Submittal Package</strong> produces the full PDF.
        </p>
      </>
    ),
  },
  {
    key: "submittal-tab",
    icon: Package,
    title: "Step 8 — Submittal Package",
    body: (
      <>
        <p>
          Step 8 is the document workbench. Four <strong>Project Documents</strong>{" "}
          are auto-generated from your current state:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li><strong>Selection Summary</strong> — Unit Schedule by Room (per-unit capacities).</li>
          <li><strong>Selection Report</strong> — engineering detail per room + chart.</li>
          <li><strong>Config Report</strong> — Variant Summary & Configuration Schedule (BOM, inclusions).</li>
          <li><strong>Submittal Package</strong> — full pack (cover + TOC + everything + drawings).</li>
        </ul>
        <p>
          Below those, one section per <strong>unit model</strong> in the
          project (e.g. "Model L-L Documents") with 6 categories: Wiring,
          P&ID, Brochures, IOM, Service Manuals, Certifications.
        </p>
      </>
    ),
  },
  {
    key: "documents",
    icon: Library,
    title: "Documents — Official vs Placeholder",
    body: (
      <>
        <p>
          Each per-model document row carries one of two badges:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
              <CheckCircle2 className="h-3 w-3" /> Official
            </span>
            {" "}— a real PDF has been uploaded by the admin. Used as-is in the
            compiled package.
          </li>
          <li>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
              <FileText className="h-3 w-3" /> Placeholder
            </span>
            {" "}— a stub generated on the fly so the flow works end-to-end.
            Replaced when the admin uploads the real document.
          </li>
        </ul>
        <p>
          Click the eye icon next to any row to preview the file in a new tab,
          then choose <strong>Merged PDF</strong> or <strong>ZIP archive</strong>{" "}
          before hitting <strong>Compile Package</strong>.
        </p>
      </>
    ),
  },
  {
    key: "single-unit",
    icon: Gauge,
    title: "Single Unit Selection — the quick path",
    body: (
      <>
        <p>
          When you only need to size one unit, skip the wizard and use{" "}
          <strong>Home → Single Unit Selection</strong>. The flow is:
        </p>
        <ol className="list-decimal pl-5 space-y-1 text-sm">
          <li>Pick a <strong>Model</strong> from the dropdown.</li>
          <li>Pick a <strong>Mode</strong>:
            <ul className="list-disc pl-5 mt-1">
              <li><em>Find max performance for a given length</em> — pick length, get max capacity at max water flow.</li>
              <li><em>Meet required performance</em> — enter cooling / heating / OA targets.</li>
            </ul>
          </li>
          <li>Confirm <strong>Design + Advanced</strong> conditions (defaults work for most cases).</li>
          <li>Hit <strong>Run Selection</strong>. Results are the same Selection Report engineers see in projects.</li>
        </ol>
      </>
    ),
  },
  {
    key: "wrap",
    icon: Beaker,
    title: "You're set!",
    body: (
      <>
        <p>That's the whole flow. A few last tips:</p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li><strong>SI / IP toggle</strong> sits in the header — flip it any time, all numbers re-render including the chart.</li>
          <li><strong>Save</strong> on the wizard's last footer button persists the project to the cloud.</li>
          <li>Stuck? Send feedback via <span className="inline-flex items-center px-1.5 py-0.5 bg-muted rounded text-xs"><MessageSquare className="h-3 w-3 mr-1" />Help → Send feedback</span> — it opens your mail client with diagnostics pre-filled.</li>
          <li>Found a bug? Same channel. We watch <span className="font-mono text-xs">support@zambahvac.com</span> closely.</li>
        </ul>
      </>
    ),
  },
];

const TOUR_SEEN_KEY = "zamba_tour_seen_v1";

export default function OnboardingTour({ open, onOpenChange }) {
  const [idx, setIdx] = useState(0);
  // Reset slide pointer every time the dialog re-opens.
  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);

  const step = STEPS[idx];
  const Icon = step?.icon || HomeIcon;
  const isLast = idx === STEPS.length - 1;
  const isFirst = idx === 0;

  const finish = () => {
    try {
      localStorage.setItem(TOUR_SEEN_KEY, "1");
    } catch { /* ignore quota */ }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); else onOpenChange(true); }}>
      <DialogContent className="max-w-2xl" data-testid="onboarding-tour">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-emerald-600 text-white shrink-0">
              <Icon className="h-5 w-5" />
            </span>
            <span>{step.title}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="space-y-3 text-sm text-foreground leading-relaxed py-2 min-h-[230px]">
          {step.body}
        </div>

        {/* Pagination dots */}
        <div className="flex justify-center gap-1 my-2">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-emerald-600" : "w-1.5 bg-border hover:bg-muted-foreground"}`}
              data-testid={`tour-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={finish}
            data-testid="tour-skip"
          >
            Skip tour
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={isFirst}
              data-testid="tour-prev"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Prev
            </Button>
            {!isLast ? (
              <Button
                size="sm"
                onClick={() => setIdx((i) => Math.min(STEPS.length - 1, i + 1))}
                className="bg-foreground hover:bg-foreground/90 text-background"
                data-testid="tour-next"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={finish}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="tour-finish"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Got it!
              </Button>
            )}
          </div>
        </div>

        <div className="text-[11px] text-muted-foreground text-center pt-1">
          Slide {idx + 1} of {STEPS.length}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Used by Home page to auto-launch on first ever visit. Returns true if the
// user has never finished the tour (= we should auto-open it).
export function shouldAutoLaunchTour() {
  try {
    return localStorage.getItem(TOUR_SEEN_KEY) !== "1";
  } catch {
    return false;
  }
}
